<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="auto-qa-teams-tab">
    <div class="main-section" v-if="!noData">
      <div class="teams-dropdown-div">
        <div class="dropdown-title">Team</div>
        <el-select ref="viewBySelect" v-model="selectedTeam" collapse-tags size="mini" class="teams-dropdown" popper-class="wy-auto-qa-dropdown-popper">
          <el-option
            v-for="item in teamList"
            @click.native="handleSelectTeam"
            :key="item.key"
            :label="item.label"
            :value="item.key"
          >
          </el-option>
        </el-select>
      </div>

      <div class="chart-section">
        <wy-auto-qa-panel-dynamic-chart ref="dynamicChart" :controller="module" :lineChartTitleMapper="lineChartTitleMapper" :waterfallChartFooterMapper="waterfallChartFooterMapper" :legends="lineChartLegends" />
      </div>
      <div class="table-title">Agents</div>
      <div class="table-section" style="overflow: hidden;" v-loading="tableLoading">
        <wy-table-list v-if="activeTab==='teams'" ref="table" :controller="module" :enableSelection="true" :noMainColumn="true" :rowClickEvent="handleRowClick" :defaultSort="{ name: sortBy, type: sortType }" :paginationSorting="paginationSorting" searchFieldName="contact reaons" :isHeaderTooltipVisible="true"/>
      </div>
      <wy-table-pagination v-if="hasPagination" :callback="pageChange" :isTableLoading="tableLoading"></wy-table-pagination>
    </div>
    <div v-else class="no-data">
      <div class="content">No data available yet for this selection.</div>
      <div class="content">Please use the global filter to make another selection with available data.</div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import wyHomeExperienceAutomationPanelDynamicChart from './panel-templates/v2/wy-auto-qa-panel-dynamic-chart.vue';
import WyTableList from '../widgets/wy-table-list';
import WyTablePagination from '../widgets/wy-table-pagination';

  export default Vue.extend({
    name: 'wy-auto-qa-teams-tab',
    props: ['parent', 'activeTab'],
    components: {
      'wy-auto-qa-panel-dynamic-chart': wyHomeExperienceAutomationPanelDynamicChart,
      'wy-table-list': WyTableList,
      'wy-table-pagination': WyTablePagination
    },
    data() {
      return {
        module: this,
        showGroupsTeamsPage: true,
        lineChartLegends: [],
        lineChartTitleMapper: {
          autoEvalScore: 'Evaluation score trend'
        },
        waterfallChartFooterMapper: {
          autoEvalScore: 'Auto evaluation sections'
        },
        selectedTeam: 'all',
        fetchData: true,
        noData: false,
        tableData: null,
        dynamicChartsLoading: true,
        tableLoading: false,
        sortBy: 'overallScore',
        sortType: 1,
        columns: []
      }
    },

    computed: {
      ...mapGetters(['dataModel', 'tableModel']),
      hasPagination() {
        return this.dataModel.page.pagination && this.dataModel.page.size < this.tableModel.totalRecs
      },

      teamList() {
        return this.parent.teams;
      }
    },

    methods: {
      ...mapMutations(['updateDataModelColumns', 'updateDataModelPagination', 'updateDataModelPageSize', 'updateDataModelPageNumber']),
      ...mapActions(['fetchAutoQAGroupTeamTableData']),

      handleSelectTeam() {
        this.fetchData = true;
        this.noData = false;
        this.parent.handlePanelChange()
      },

      handleRowClick(rowIndex, item) {
        this.$router.push({ name: 'autoQAEvaluationAgent', params: { agent: item, teamId: this.selectedTeam, filterDisplayName: document.querySelector('.nav_top .component.interactions-filtering-widget .el-dropdown-subtitle').innerHTML}});
      },

      generateColumns() {
        let sections = this.tableModel.sections;
        this.columns = [
        { label: 'Agent', name: 'displayName', mandatory: true, selected: false, sortable: true, hasSubtitle: false, group: 'Agent', align: 'left', minWidth: 383, maxWidth: 383, fixed: true, mainColumn: true, copyable: false, isEditTopicName: false, extendableField: true },
        { label: 'Volume', name: 'volume', selected: true, sortable: true, hasSubtitle: false, group: 'Volume', sortBy: 'volume', minWidth: 100, suffix: '', align: 'center' },
        { label: 'AES', name: 'overallScoreDisplay', selected: true, sortable: true, hasSubtitle: false, group: 'Average auto evaluation score', sortBy: 'overallScore',minWidth: 180, align: 'center' },
        ];
        if(sections && sections.length > 0) {
          sections.forEach(section => {
            let obj = { 
              label: section, 
              name: (section.replace(/\s(.)/g, function (a) {
                return a.toUpperCase();
              })
              .replace(/\s/g, '')
              .replace(/^(.)/, function (b) {
                  return b.toLowerCase();
              })) + 'Display', 
              selected: true, 
              sortable: true, 
              hasSubtitle: false, 
              group: section, 
              sortBy: section, 
              minWidth: 120, 
              suffix: '', 
              align: 'center' 
            }
            this.columns.push(obj)
          })
        }
      },

      async fetchTeamTableSummary() {
        if(this.fetchData) {
          this.updateDataModelPageNumber(1)
          this.sortBy = 'overallScore'
          let options = this.prepareOptions()
          this.parent.groupsTeamsTableLoading = true;
          await this.fetchAutoQAGroupTeamTableData(options);
          this.noData = (this.tableModel.results.length == 0) ? true : false 
          this.generateColumns();
          this.updateDataModelColumns({ columns: this.columns });
          this.$refs && this.$refs.table && this.$refs.table.refreshTable({ disablePageLoading: true });
          this.tableData = JSON.parse(JSON.stringify(this.tableModel))
          this.tableLoading = false;
          this.parent.groupsTeamsTableLoading = false;
          this.fetchData = false;
        }
      },

      async pageChange(pageNumber) {
        this.updateDataModelPageNumber(pageNumber)
        let options = this.prepareOptions()
        this.tableLoading = true;
        await this.fetchAutoQAGroupTeamTableData(options);
        this.updateDataModelColumns({ columns: this.columns });
        this.$refs.table && this.$refs.table.refreshTable({ disablePageLoading: true });
        this.tableLoading = false;
      },

      async paginationSorting({ sortBy, sortType }) {
        this.sortBy = sortBy;
        this.sortType = sortType
        this.pageChange(1)
      },

      prepareOptions() {
        let selectedTeams = [];
        if(this.selectedTeam == 'all') {
          this.parent.teams.forEach(team => {
            if(team.key !== 'all') {
              selectedTeams.push(team.key)
            }
          })
        }
        else {
          selectedTeams.push(this.selectedTeam)
        }
        let options = {
          type: 'agentTeams',
          value: selectedTeams,
          tableType: 'team',
          sortColumn: this.sortBy,
          sorting: this.sortType ? 'desc' : 'asc',
          pageSize: this.dataModel.page.size,
          pageNumber: this.dataModel.page.number
        }
        return options;
      }
    },

    created() {
      this.updateDataModelPagination(true)
      this.updateDataModelPageSize(10)
    },

    async mounted() {
      if(this.parent.teamId) {
        this.selectedTeam = this.parent.teamId;
      }
      else {
        this.selectedTeam = 'all';
      }
      this.mockData = [
        {
          displayName: "Name 1",
          totalVolume: 34.58,
          aaes: 3.23,
          greeting: 69,
          empathy: 43.6,
          professionalism: 93,
          closing: 76.8,
          customerEffort: 33,
          resolution: 23
        },
        {
          displayName: "Name 2",
          totalVolume: 34.58,
          aaes: 3.23,
          greeting: 69,
          empathy: 43.6,
          professionalism: 93,
          closing: 76.8,
          customerEffort: 33,
          resolution: 23
        }
      ]
    },
  })

</script>

<style lang="scss">
  .auto-qa-teams-tab {

    .teams-dropdown-div {
      padding-top: 0px;
      padding-right: 40px;
      padding-bottom: 0px;
      margin-left: auto;
      min-width: unset !important;

      .el-select.teams-dropdown.el-select--mini {
        width: 7.86vw !important;
        min-width: 140px;
        border: 1px #d9d9d9 solid;
        border-radius: 5px;
      }
      .el-select.teams-dropdown.el-select--mini.blue-border {
        border: 1px #106fbc solid !important;
      }
      .el-select.teams-dropdown.el-select--mini:active {
        border: 1px solid #1c4db2 !important;
      }

      input.el-input__inner {
        border-width: 0px 0px 1px 0px !important;
        border-radius: 5px;
        padding-left: 10px;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
      }
      .el-input__inner {
        border: 1px solid transparent !important;
        &:hover {
          background: #f4f6f8;
        }
      }

      .dropdown-title {
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        padding-bottom: 5px;
      }
    }
    .table-title {
      font-size: 17.5px;
      font-style: normal;
      font-weight: 700;
      padding-bottom: 15px;
    }
    .chart-section {
      margin-top: 20px;
    }
    .table-section.el-loading-parent--relative {
      height: 400px;
    }
    .table-section {
      border: solid 1px #DFE4E6;
      border-radius: 5px;
      .wy-table-list {
        .wy-table-fixed tbody td:nth-last-child(1) {
          padding-right: 10px;
          text-decoration: underline;
          color: #006FE7;
        }
        thead {
          background-color: #FFF;
          .wy-sub-header th.lastChild {
            border-right-color: #FFF;
          }
        }
        tbody tr {
          td.last-child {
            border-right: 1px solid transparent;
            .el-no-data {
              justify-content: center !important;
            }
          }
          td div {
            justify-content: left;
          }
          
        }
        .wy-table-main {
          .wy-sub-header th {
            text-align: left !important;
          }
          .wy-sub-header th.lastChild {
            border-right-color: #FFF;
          }
          
          thead tr :first-child {
            padding-left: 5px;
          }
          thead th {
            background-color: #FFF;
          }
          tbody {
            height: fit-content;
            &::-webkit-scrollbar {
              width: 20px;
              height: 20px;
            }
            &::-webkit-scrollbar-thumb {
              border-radius: 180px;
              border: 5px solid transparent;
              border-top: 6px solid transparent;
              background-clip: content-box;
              background-color: #CAD2DA;
            }
            &::-webkit-scrollbar-track {
              background:#FFF;
              border-top: 1px solid #CAD2DA;
            }
            &::-webkit-scrollbar:vertical {
              width: 14px;
            }
            &::-webkit-scrollbar-thumb:vertical {
              border-radius: 160px;
              border: 2px solid transparent;
              border-left: 4px solid transparent;
              background-clip: content-box;
              background-color: #CAD2DA;
            }
            &::-webkit-scrollbar-track:vertical {
              border-left: 1px solid #CAD2DA;
              border-top: none !important;
            }
            tr td:first-child {
              padding-left: 12px;
            }
          }
        }
        .wy-table-tail {
          display: table-cell;
          min-width: 0px;
        }
      }
    }
    .no-data {
      padding-top: 50px;
      text-align: center;
      width: 100%;
      .content {
        padding-bottom: 5px;
        font-size: 17.5px;
        font-style: normal;
        font-weight: 400;
      }
    }
  }
  
  .wy-auto-qa-dropdown-popper {
    .el-select-dropdown__item.selected {
      color: #1a55ad !important;
      background-color: #e8f1ff !important;
      font-weight: 500;
      padding-bottom: 5px;
    }
  }
</style>